/**
 * usePerformanceWindow — CR-5563
 *
 * Re-fetches ONE supplier profile for a chosen KPI reporting period, for the
 * Performance tab alone.
 *
 * Why a second fetch rather than adding dates to `useSupplierDetail`: that hook
 * fetches the profile once and hands the same object to Company Overview,
 * Orders, Quality and Credit Scores. Putting a date window on it would silently
 * re-scope every number on tabs this ticket never mentioned. The cost is one
 * extra request when the Performance tab opens; the alternative is a regression
 * surface across the whole supplier record.
 *
 * @param supplierCompanyId The supplier's COMPANY id (`SupplierProfile.company`).
 *                          `GET /suppliers/profiles/<id>/` looks profiles up by
 *                          company_id, so the profile's own id is the wrong key
 *                          (CR-8681: it 404'd wherever the two ids differ).
 * @param periodWindow      Resolved dates for the selected period.
 * @param reloadToken       Bump to re-request the same window after a failure.
 * @returns the windowed profile, a loading flag, and any error.
 */
import { useEffect, useRef, useState } from 'react';
import { suppliersAPI, SupplierProfile } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { PerformanceWindow } from './performancePeriods';

interface UsePerformanceWindowResult {
  profile: SupplierProfile | null;
  loading: boolean;
  error: string | null;
}

export function usePerformanceWindow(
  supplierCompanyId: number | undefined,
  periodWindow: PerformanceWindow,
  reloadToken: number = 0,
): UsePerformanceWindowResult {
  const { viewLevel, getViewQueryParams } = useView();
  const [profile, setProfile] = useState<SupplierProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Every windowed metric is scoped by company/site as well as by date. The
  // scope params must travel with the dates or the tab silently widens to
  // every customer. Serialised so the effect re-runs when the view switches.
  const viewParams = getViewQueryParams();
  const viewParamsKey = JSON.stringify({ viewLevel, ...viewParams });

  // Guards against a slow earlier request landing after a faster later one and
  // overwriting it — easy to trigger by clicking through the presets quickly.
  const requestSeqRef = useRef(0);

  useEffect(() => {
    // The tab unmounts with its Radix TabsContent, so there is no separate
    // 'enabled' gate to keep hidden tabs from fetching.
    if (!supplierCompanyId) return;

    const seq = ++requestSeqRef.current;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await suppliersAPI.getProfile(supplierCompanyId, {
          view_level: viewLevel,
          ...getViewQueryParams(),
          date_from: periodWindow.dateFrom,
          date_to: periodWindow.dateTo,
        });
        // Drop the response if a newer request has already been issued, or the
        // tab unmounted while this one was in flight.
        if (cancelled || seq !== requestSeqRef.current) return;
        setProfile(response.data);
        setError(null);
      } catch (err: unknown) {
        if (cancelled || seq !== requestSeqRef.current) return;
        console.error('Failed to load windowed supplier performance:', err);
        setError(getApiErrorMessage(err, 'Failed to load performance for this period'));
      } finally {
        if (!cancelled && seq === requestSeqRef.current) setLoading(false);
      }
    };

    void run();
    return () => { cancelled = true; };
    // getViewQueryParams is re-created each render; viewParamsKey captures its
    // value, which is what actually matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierCompanyId, periodWindow.dateFrom, periodWindow.dateTo, viewParamsKey, reloadToken]);

  return { profile, loading, error };
}
